import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  RecurringTaskActivateDocument,
  RecurringTaskCreateDocument,
  RecurringTaskDeactivateDocument,
  RecurringTaskDeleteDocument,
  RecurringTaskGetByIdDocument,
  RecurringTaskListPaginatedDocument,
  RecurringTaskUpdateDocument,
} from "api/generated";

import { Optional } from "config/types";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IRecurringTaskModel,
  IRecurringTaskCreateVars,
  IRecurringTaskPaginatedResponse,
  IRecurringTaskPaginatedVars,
  IRecurringTaskUpdateVars,
} from "./types";

export async function getRecurringTask(
  recurringTaskId: number
): Promise<Optional<IRecurringTaskModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: RecurringTaskGetByIdDocument,
      variables: {
        id: recurringTaskId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.recurringTaskGetById as Optional<IRecurringTaskModel>;
  } catch (error) {
    throw error;
  }
}

export async function getRecurringTaskPaginated(
  variables: IRecurringTaskPaginatedVars
): Promise<Optional<IRecurringTaskPaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: RecurringTaskListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data
      ?.recurringTaskListPaginated as Optional<IRecurringTaskPaginatedResponse>;
  } catch (error) {
    throw error;
  }
}

export async function createRecurringTask(
  variables: IRecurringTaskCreateVars
): Promise<Optional<{ recurringTaskId: number }>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: RecurringTaskCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "RecurringTask",
      fieldNames: ["recurringTaskListPaginated"],
    });

    return res?.data?.recurringTaskCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateRecurringTask(
  variables: IRecurringTaskUpdateVars
): Promise<Optional<{ recurringTaskId: number }>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: RecurringTaskUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "RecurringTask",
      id: variables.recurringTaskId.toString(),
      fieldNames: ["recurringTaskListPaginated", "recurringTaskGetById"],
    });

    return res?.data?.recurringTaskUpdate;
  } catch (error) {
    throw error;
  }
}

export async function activateRecurringTask(
  recurringTaskId: number
): Promise<Optional<boolean>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: RecurringTaskActivateDocument,
      variables: {
        id: recurringTaskId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "RecurringTask",
      id: recurringTaskId.toString(),
      fieldNames: ["recurringTaskListPaginated", "recurringTaskGetById"],
    });

    return res?.data?.recurringTaskActivate;
  } catch (error) {
    throw error;
  }
}

export async function deactivateRecurringTask(
  recurringTaskId: number
): Promise<Optional<boolean>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: RecurringTaskDeactivateDocument,
      variables: {
        id: recurringTaskId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "RecurringTask",
      id: recurringTaskId.toString(),
      fieldNames: ["recurringTaskListPaginated", "recurringTaskGetById"],
    });

    return res?.data?.recurringTaskDeactivate;
  } catch (error) {
    throw error;
  }
}

export async function deleteRecurringTask(
  recurringTaskId: number
): Promise<Optional<boolean>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: RecurringTaskDeleteDocument,
      variables: {
        id: recurringTaskId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "RecurringTask",
      id: recurringTaskId.toString(),
      fieldNames: ["recurringTaskListPaginated"],
    });

    return res?.data?.recurringTaskDelete;
  } catch (error) {
    throw error;
  }
}
